/** Entity profile — the universal two-column shell (person + organization).
 * PRIMARY (wider): name header → profile card (employment/signature/aliases or
 * people/domains) → timeline. SECONDARY: linked records, then relationships.
 * Full browser width by design (fluid grid, no reading-column clamp). */
import { useMemo, useState } from 'react';
import styles from './crm.module.css';
import { ApiError } from '../../api/client';
import { isAdmin } from '../../auth/admins';
import { useAuthUser } from '../../auth/useAuthUser';
import {
  useAddPersonEmployment,
  useRejectCrmField,
  useCrmEmployment,
  useCrmOrgPeople,
  useCrmProfile,
  useEditPersonTitle,
  useEditCrmField,
  useEntityAttributeHistory,
  useReclassifyShared,
  useRenameEntity,
  type CrmEntityKind,
} from '../../hooks/useCrm';
import { CrmAddRoleModal } from './CrmAddRoleModal';
import { CrmAttributeProvenance } from './CrmAttributeProvenance';
import {
  useEnrichmentAccess,
  useEnrichmentStatus,
  useEnrichProfile,
} from '../../hooks/useCrmEnrichment';
import { CrmEnrichButton } from './CrmEnrichButton';
import { CrmEnrichmentCard } from './CrmEnrichmentCard';
import { CrmLinkedRecords } from './CrmLinkedRecords';
import { CrmMergeModal } from './CrmMergeModal';
import { CrmMergeUndoModal } from './CrmMergeUndoModal';
import { CrmPopover } from './CrmPopover';
import { CrmRelationships } from './CrmRelationships';
import { CrmRenameModal } from './CrmRenameModal';
import { CrmTimeline } from './CrmTimeline';
import { domainHref, fmtShortDate, isPersonSubtype, subtypeLabel } from './crmFormat';

const errText = (e: unknown, fb: string) => {
  if (e instanceof ApiError) {
    if (typeof e.detail === 'string' && e.detail) return e.detail;
    if (e.detail && typeof e.detail === 'object' && 'detail' in e.detail) {
      return String((e.detail as { detail: unknown }).detail);
    }
    return e.message || fb;
  }
  return fb;
};

/** Validate a discovered LinkedIn value into a safe external href (integration F7) — only an https
 * linkedin.com URL (or a bare linkedin.com/… path) becomes a link; anything else renders as plain text
 * so we never emit an attacker-controlled or non-LinkedIn href. */
function linkedinHref(v: string | null | undefined): string | null {
  const s = (v || '').trim();
  if (/^https:\/\/([a-z0-9-]+\.)?linkedin\.com\//i.test(s)) return s;
  if (/^(www\.)?linkedin\.com\//i.test(s)) return `https://${s.replace(/^www\./i, '')}`;
  return null;
}

interface Props {
  kind: CrmEntityKind;
  id: number;
  onBack: () => void;
  openPerson: (id: number) => void;
  openOrg: (id: number) => void;
  /** The full, deep-linkable message PAGE (`?sel=msg:N`). Threaded to the timeline, whose opened
   *  rows render the body in place and offer this as "Full view ↗" — the profile no longer opens
   *  the mail modal at all (timeline-inline spec §3.3). */
  openMsgFullView: (id: number) => void;
}

export function CrmProfile({ kind, id, onBack, openPerson, openOrg, openMsgFullView }: Props) {
  const profile = useCrmProfile(kind, id);
  // De-pilot access split: the ✦ history/corrections machinery stays enrichment-admin-only; the
  // enriched VALUES render for every tab viewer via the signature/profile fallbacks.
  const canCurate = useEnrichmentAccess().data?.pilot === true;
  const attrHist = useEntityAttributeHistory(kind, id, canCurate);
  const orgPeople = useCrmOrgPeople(kind === 'organization' ? id : null);
  const orgStatus = useEnrichmentStatus(
    'org', kind === 'organization' ? id : null, kind === 'organization' && canCurate);
  const enrichOrg = useEnrichProfile();
  const me = useAuthUser();
  const admin = isAdmin(me.email);
  const rename = useRenameEntity(kind, id);
  const reclassify = useReclassifyShared(id);
  const addRole = useAddPersonEmployment(id);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [renaming, setRenaming] = useState(false);
  // PM-9: transient modals, so `useState` and NOT URL state (frontend rules).
  const [merging, setMerging] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  // The add-role modal carries its OWN error, like the title editor: `actionErr` renders in the card
  // beneath the backdrop, where a modal's failure would be invisible.
  const [roleErr, setRoleErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const orgTypes = useMemo<string[]>(() => {
    if (kind !== 'organization') return [];
    const raw = attrHist.data?.attributes.org_types?.current?.value;
    if (!raw) return [];
    try {
      const value = JSON.parse(raw);
      return Array.isArray(value) ? value.map(String) : [];
    } catch {
      return [];
    }
  }, [kind, attrHist.data?.attributes.org_types?.current?.value]);
  const orgEnriched = kind === 'organization' && orgStatus.data?.state === 'enriched';
  const orgEnriching = kind === 'organization'
    && (enrichOrg.isPending || orgStatus.data?.state === 'queued');

  const enqueueOrg = () => enrichOrg.mutate({ kind: 'org', org_id: id });

  const doReclassify = () => {
    if (!window.confirm(
      'Mark this record as a shared / functional mailbox (list or group)?\n\n'
      + 'Its person profile is removed and its emails & meetings will resolve to the '
      + 'organization only — not to a person.')) return;
    setActionErr(null);
    reclassify.mutate(undefined, {
      onSuccess: onBack,  // the person is retracted → its profile no longer exists
      onError: (e) => setActionErr(errText(e, 'Could not set as list.')),
    });
  };

  if (profile.isLoading) {
    return (
      <>
        <button className={styles.backButton} onClick={onBack}>← Back</button>
        <div className={styles.loading}>Loading profile…</div>
      </>
    );
  }
  if (profile.error != null || !profile.data) {
    const notFound = profile.error instanceof ApiError && profile.error.status === 404;
    return (
      <>
        <button className={styles.backButton} onClick={onBack}>← Back</button>
        <div className={styles.error}>
          {notFound
            ? `This ${kind === 'person' ? 'person' : 'organization'} doesn't exist or isn't visible to you.`
            : 'Failed to load profile.'}
        </div>
      </>
    );
  }

  const p = profile.data;
  // The header's domain is a link like every other domain on the page; the rest stays plain text.
  const primaryDomainHref = kind === 'organization' ? domainHref(p.primary_domain) : null;
  const metaBits = [
    kind === 'person' ? p.primary_email : (primaryDomainHref ? null : p.primary_domain),
    p.permission_class,
  ].filter(Boolean);

  return (
    <>
      <button className={styles.backButton} onClick={onBack}>← Back</button>
      <div className={styles.cols}>
        <div>
          <div className={styles.card}>
            <div className={styles.entityHeaderRow}>
              <h2 className={styles.entityName}>
                {p.primary_name || '(unnamed)'}
                {kind === 'person' && canCurate
                  && <CrmAttributeProvenance attr={attrHist.data?.attributes.display_name} />}
                {orgEnriched && (
                  <span className={styles.orgEnrichMark} title="Machine-enriched profile"
                        aria-label="Machine-enriched profile">✦</span>
                )}
                {/* ⛔ A BUTTON ONLY FOR AN ADMIN (PM-9). For everyone else this renders exactly as
                    it always has — a count, not a control. Ungated it would hand a non-admin an
                    affordance that opens and then 403s, and the modal's counterpart panel exposes
                    precisely the per-record detail the review queue routes through `scoped_select`.
                    Harmless until now only because the count always read 0 and the chip did nothing. */}
                {p.possible_duplicate_count > 0 && (admin ? (
                  <button type="button" className={styles.chipDupButton}
                          title="Look at possible duplicates of this record"
                          onClick={() => { setActionErr(null); setMerging(true); }}>
                    ⚑ {p.possible_duplicate_count} possible duplicate
                    {p.possible_duplicate_count === 1 ? '' : 's'}
                  </button>
                ) : (
                  <span className={`${styles.chip} ${styles.chipDup}`}>
                    ⚑ {p.possible_duplicate_count} possible duplicate
                    {p.possible_duplicate_count === 1 ? '' : 's'}
                  </span>
                ))}
                {p.merged_into_id != null && (
                  <span className={`${styles.chip} ${styles.chipMuted}`}>merged</span>
                )}
                {kind === 'person' && !isPersonSubtype(p.subtype) && (
                  <span className={`${styles.chip} ${styles.chipMuted}`}>{subtypeLabel(p.subtype)}</span>
                )}
              </h2>
              <div className={styles.entityHeaderActions}>
                {kind === 'organization' && canCurate && (
                  orgEnriched ? (
                    <span className={styles.orgEnrichedBadge}
                          title={orgStatus.data?.finished_at || 'Enriched profile'}>
                      {orgStatus.data?.finished_at
                        ? 'enriched ' + orgStatus.data.finished_at.slice(0, 10)
                        : 'enriched'}
                    </span>
                  ) : (
                    <button type="button" className={styles.orgEnrichButton}
                            onClick={enqueueOrg} disabled={orgEnriching}
                            title={orgStatus.data?.last_error || 'Enrich this company profile'}>
                      {orgEnriching ? 'Enriching…' : 'Enrich'}
                    </button>
                  )
                )}
                {/* ⛔ LEFT EXACTLY AS IT WAS (PM-9), and the reason is worth writing down because the
                    build plan asked for the opposite. It wanted this gate relaxed so "Undo this
                    merge…" could live on the merged-away record — but a merged record's PROFILE DOES
                    NOT EXIST for any caller: `governance.scoped_select` filters `deleted_at IS NULL`
                    at the choke point and `merge._tombstone` sets it, so the profile route 404s.
                    This condition is unreachable in production, relaxing it would have changed
                    nothing, and the undo lives on the SURVIVOR — which is what the slice's own
                    acceptance says. Pinned backend-side by
                    `test_a_tombstone_is_existence_safe_404_so_the_undo_lives_on_the_SURVIVOR`. */}
                {p.merged_into_id == null && (
                  <button
                    className={styles.entityMenuBtn}
                    aria-label="Entity actions"
                    onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                  >⋯</button>
                )}
              </div>
            </div>
            <div className={styles.entityMeta}>
              {primaryDomainHref && (
                <>
                  <a href={primaryDomainHref} target="_blank" rel="noopener noreferrer"
                     className={styles.entityMetaLink}>{p.primary_domain}</a>
                  {metaBits.length > 0 && ' · '}
                </>
              )}
              {metaBits.join(' · ')}
            </div>
            {kind === 'organization' && (
              <div className={styles.companySummaryFields}>
                {orgTypes.length > 0 && (
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Org types</span>
                    <span className={styles.fieldValue}>
                      {orgTypes.map((type) => (
                        <span key={type} className={styles.companyInfoChip}>{type}</span>
                      ))}
                    </span>
                  </div>
                )}
                <CrmEnrichmentCard kind="org" id={id} embedded />
                {(orgPeople.data?.aliases.length ?? 0) > 0 && (
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Also known as</span>
                    <span className={styles.fieldValue}>
                      {orgPeople.data!.aliases.map((alias) => (
                        <span key={alias} className={styles.companyInfoChip}>{alias}</span>
                      ))}
                    </span>
                  </div>
                )}
                {(orgPeople.data?.domains.length ?? 0) > 1 && (
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Domains</span>
                    <span className={styles.fieldValue}>
                      {orgPeople.data!.domains.map((domain) => {
                        const href = domainHref(domain);
                        return href ? (
                          <a key={domain} href={href} target="_blank" rel="noopener noreferrer"
                             className={`${styles.companyInfoChip} ${styles.companyInfoChipLink}`}
                             title={`Open ${domain} in a new tab`}>{domain}</a>
                        ) : (
                          <span key={domain} className={styles.companyInfoChip}>{domain}</span>
                        );
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* the rename modal shows its own error; surface here only the card-level actions (reclassify) */}
            {actionErr && !renaming && <div className={styles.error}>{actionErr}</div>}
          </div>
          {menuAnchor && (
            <CrmPopover anchorEl={menuAnchor} align="right" onClose={() => setMenuAnchor(null)}>
              <button
                role="menuitem"
                className={styles.popoverItem}
                onClick={() => { setMenuAnchor(null); setActionErr(null); setRenaming(true); }}
              >Edit name</button>
              {admin && (
                <button
                  role="menuitem"
                  className={styles.popoverItem}
                  onClick={() => { setMenuAnchor(null); setActionErr(null); setMerging(true); }}
                >Merge…</button>
              )}
              {/* ⚠ "a merge", not "this merge", and offered on the SURVIVOR — see the gate above for
                  why the tombstone is not a reachable surface. Whether a merge EXISTS is resolved by
                  the modal rather than here, deliberately: the eligibility verdict comes from
                  `undo.plan_undo`, a real planner doing many reads (the same cost the shipped undo
                  PREVIEW route already pays), and paying it on every admin profile load just to
                  decide whether to draw one menu item would be the wrong trade. The modal says "This
                  record has not been merged" when there is nothing to reverse. */}
              {admin && (
                <button
                  role="menuitem"
                  className={styles.popoverItem}
                  onClick={() => { setMenuAnchor(null); setActionErr(null); setUndoing(true); }}
                >Undo a merge…</button>
              )}
              {/* Person profiles only: an affiliation is recorded FROM the person (the org's People
                  card is the same edge read from the other end). No admin gate — the server takes the
                  same `require_crm_privileged` as Edit name, because this curates an association, it
                  does not change what anybody may read. */}
              {kind === 'person' && (
                <button
                  role="menuitem"
                  className={styles.popoverItem}
                  onClick={() => { setMenuAnchor(null); setRoleErr(null); setAddingRole(true); }}
                >Add role…</button>
              )}
              {kind === 'organization' && canCurate && orgEnriched && (
                <button
                  role="menuitem"
                  className={styles.popoverItem}
                  disabled={orgEnriching}
                  onClick={() => { setMenuAnchor(null); enqueueOrg(); }}
                >Re-enrich</button>
              )}
              {kind === 'person' && admin && (
                <button
                  role="menuitem"
                  className={`${styles.popoverItem} ${styles.popoverDanger}`}
                  onClick={() => { setMenuAnchor(null); doReclassify(); }}
                >Set as list / shared</button>
              )}
            </CrmPopover>
          )}
          {merging && (
            <CrmMergeModal
              kind={kind}
              id={id}
              name={p.primary_name}
              onClose={() => setMerging(false)}
              onMerged={(survivorId) => {
                setMerging(false);
                // The record we were looking at may BE the tombstone now, so land on the survivor
                // rather than leaving a dead profile on screen — the "which one survived?" confusion
                // this slice exists to remove would otherwise be re-created at the last step.
                if (survivorId !== id) {
                  if (kind === 'person') openPerson(survivorId); else openOrg(survivorId);
                }
              }}
            />
          )}
          {undoing && (
            <CrmMergeUndoModal
              kind={kind}
              id={id}
              onClose={() => setUndoing(false)}
              onUndone={() => setUndoing(false)}
            />
          )}
          {renaming && (
            <CrmRenameModal
              title={kind === 'person' ? 'Edit name' : 'Edit organization name'}
              initialName={p.primary_name || ''}
              saving={rename.isPending}
              error={actionErr ?? undefined}
              onClose={() => setRenaming(false)}
              onSave={(name) => {
                setActionErr(null);
                rename.mutate(name, {
                  onSuccess: () => setRenaming(false),
                  onError: (e) => setActionErr(errText(e, 'Could not save the name.')),
                });
              }}
            />
          )}
          {addingRole && (
            <CrmAddRoleModal
              personName={p.primary_name || 'this person'}
              saving={addRole.isPending}
              error={roleErr ?? undefined}
              onClose={() => setAddingRole(false)}
              onSave={(body) => {
                setRoleErr(null);
                addRole.mutate(body, {
                  onSuccess: () => setAddingRole(false),
                  onError: (e) => setRoleErr(errText(e, 'Could not add the role.')),
                });
              }}
            />
          )}
          {kind === 'person' ? (
            <PersonCard personId={id} openOrg={openOrg} />
          ) : (
            <OrgCard people={orgPeople} openPerson={openPerson} />
          )}
          {kind === 'person' && <CrmEnrichmentCard kind="person" id={id} />}
          {/* The separate Notes card is GONE (2026-08-27): every note it listed already renders in
              the Timeline below, so the profile printed each one twice. Creation moved with it — the
              Timeline header's `+ New note`, which is now the only way to write a note on a person or
              org, and which also owns the `note` overlay the card used to mount. */}
          <CrmTimeline kind={kind} id={id} hostLabel={p.primary_name} openMsgFullView={openMsgFullView}
                       openPerson={openPerson} />
        </div>
        <div>
          <CrmLinkedRecords kind={kind} id={id} />
          <CrmRelationships kind={kind} id={id} />
        </div>
      </div>
    </>
  );
}

function PersonCard({ personId, openOrg }: { personId: number; openOrg: (id: number) => void }) {
  const emp = useCrmEmployment(personId);
  const canCurate = useEnrichmentAccess().data?.pilot === true;
  const attr = useEntityAttributeHistory('person', personId, canCurate).data?.attributes;
  const editTitle = useEditPersonTitle(personId);
  const [titleModal, setTitleModal] = useState(false);
  const [titleErr, setTitleErr] = useState<string | null>(null);
  const data = emp.data;
  // Title renders from the ledger projection (so an inline user edit — a tier-2 obs — shows immediately
  // and matches the ✦ chip); falls back to the signature card / current employment row (F4/F7).
  const title = attr?.title?.current?.value || data?.signature?.title
    || data?.rows.find((r) => r.is_current)?.title || null;
  const seniority = attr?.seniority?.current?.value || null;
  const group = attr?.group?.current?.value || data?.group || null;
  const [groupModal, setGroupModal] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  // D-7: the department/practice group, SPLIT from the title (ledger winner first, card fallback —
  // same precedence as title). Without this row the split was invisible even once it landed.
  const division = attr?.division?.current?.value || data?.signature?.division || null;
  // D-2 phase 1: per-field Reject (enrichment-sourced values only; the component gates on source).
  const rejectField = useRejectCrmField(personId);
  const rejectFor = (key: string) => (note: string) =>
    rejectField.mutate({ key, note: note || undefined });
  // D-2 phase 2: per-field inline Edit (any source — a human edit is corrected by another edit).
  const editField = useEditCrmField(personId);
  const editFor = (key: string) => (value: string) => editField.mutate({ key, value });
  const SENIORITY_OPTIONS = ['c_suite', 'partner_md', 'vp_director', 'manager_ic', 'unknown'] as const;
  const linkedin = attr?.linkedin?.current?.value || data?.signature?.linkedin || null;
  const liHref = linkedinHref(linkedin);
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>Profile</div>
      <CrmEnrichButton kind="person" id={personId} />
      {emp.isLoading && <div className={styles.loading}>Loading…</div>}
      {emp.error != null && <div className={styles.error}>Failed to load profile details.</div>}
      {data && (
        <>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Title</span>
            <span className={styles.fieldValue}>
              {title || <span className={styles.subtle}>—</span>}
              {canCurate && <CrmAttributeProvenance attr={attr?.title} onReject={rejectFor('title')} rejectPending={rejectField.isPending} onEdit={editFor('title')} editPending={editField.isPending} />}
              {canCurate && <button
                type="button"
                className={styles.inlineEditBtn}
                title="Edit title"
                aria-label="Edit title"
                onClick={() => { setTitleErr(null); setTitleModal(true); }}
              >✎</button>}
            </span>
          </div>
          {titleModal && (
            <CrmRenameModal
              title="Edit title"
              initialName={title || ''}
              saving={editTitle.isPending}
              error={titleErr ?? undefined}
              onClose={() => setTitleModal(false)}
              onSave={(v) => {
                setTitleErr(null);
                editTitle.mutate(v, {
                  onSuccess: () => setTitleModal(false),
                  onError: (e) => setTitleErr(errText(e, 'Could not save the title.')),
                });
              }}
            />
          )}
          {division && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Division</span>
              <span className={styles.fieldValue}>
                {division}
                {canCurate && <CrmAttributeProvenance attr={attr?.division} onReject={rejectFor('division')} rejectPending={rejectField.isPending} onEdit={editFor('division')} editPending={editField.isPending} />}
              </span>
            </div>
          )}
          {(group || canCurate) && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Group</span>
              <span className={styles.fieldValue}>
                {group || <span className={styles.subtle}>—</span>}
                {canCurate && <CrmAttributeProvenance attr={attr?.group} />}
                {canCurate && <button type="button" className={styles.inlineEditBtn}
                  aria-label="Edit group"
                  onClick={() => { setGroupError(null); setGroupModal(true); }}>✎</button>}
              </span>
            </div>
          )}
          {groupModal && <CrmRenameModal
            title="Edit group" initialName={group || ''} saving={editField.isPending}
            error={groupError ?? undefined} onClose={() => setGroupModal(false)}
            onSave={(value) => {
              setGroupError(null);
              editField.mutate({ key: 'group', value }, {
                onSuccess: () => setGroupModal(false),
                onError: (e) => setGroupError(errText(e, 'Could not save the group.')),
              });
            }}
          />}
          {seniority && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Seniority</span>
              <span className={styles.fieldValue}>
                {seniority}
                {canCurate && <CrmAttributeProvenance attr={attr?.seniority} onReject={rejectFor('seniority')} rejectPending={rejectField.isPending} onEdit={editFor('seniority')} editPending={editField.isPending} editOptions={SENIORITY_OPTIONS} />}
              </span>
            </div>
          )}
          {data.rows.length > 0 && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Employment</span>
              <span className={styles.fieldValue}>
                {data.rows.map((r, i) => (
                  <div key={`${r.org_id}-${i}`}>
                    {r.org_id != null ? (
                      <a
                        href={`/crm?sel=${encodeURIComponent(`org:${r.org_id}`)}`}
                        onClick={(e) => {
                          // let modified clicks use the (real) href — new tab / copy link
                          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                          e.preventDefault();
                          openOrg(r.org_id!);
                        }}
                      >
                        {r.org_name || `org #${r.org_id}`}
                      </a>
                    ) : (
                      r.org_name || '(unknown org)'
                    )}
                    {' · '}
                    {fmtShortDate(r.valid_from)} – {r.is_current ? 'present' : fmtShortDate(r.valid_to)}
                    {r.is_current && (
                      <>
                        {' '}
                        <span className={`${styles.chip} ${styles.chipCurrent}`}>current</span>
                      </>
                    )}
                  </div>
                ))}
              </span>
            </div>
          )}
          {(data.signature?.phone || linkedin) && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Contact</span>
              <span className={styles.fieldValue}>
                {data.signature?.phone && <span>{data.signature.phone}</span>}
                {data.signature?.phone && linkedin && ' · '}
                {linkedin && (liHref ? (
                  <a href={liHref} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                ) : (
                  <span>{linkedin}</span>
                ))}
                {linkedin && canCurate && <CrmAttributeProvenance attr={attr?.linkedin} onReject={rejectFor('linkedin')} rejectPending={rejectField.isPending} onEdit={editFor('linkedin')} editPending={editField.isPending} />}
              </span>
            </div>
          )}
          {data.aliases.length > 0 && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Also seen as</span>
              <span className={styles.fieldValue}>
                {data.aliases.map((a) => (
                  <span key={a} className={`${styles.chip} ${styles.chipMuted}`} style={{ marginRight: 4 }}>
                    {a}
                  </span>
                ))}
              </span>
            </div>
          )}
          {!title && !seniority && !linkedin && data.rows.length === 0 && data.aliases.length === 0 && (
            <div className={styles.empty}>Sparse until enrichment runs — nothing extracted yet.</div>
          )}
          <div className={styles.hint}>
            Title/contact from email-signature extraction; employment from the resolver.
          </div>
        </>
      )}
    </div>
  );
}

const PEOPLE_PAGE = 10;
type PeopleSort = 'interactions' | 'alpha';

function OrgCard({ people, openPerson }: {
  people: ReturnType<typeof useCrmOrgPeople>;
  openPerson: (id: number) => void;
}) {
  const data = people.data;
  const [sort, setSort] = useState<PeopleSort>('interactions');
  const [showAll, setShowAll] = useState(false);

  // Split real people from distribution lists / shared / automated mailboxes (Ring-1 hygiene):
  // people get the sorted + top-10 treatment; the rest render in a separate lower section.
  const personRows = useMemo(
    () => (data?.rows ?? []).filter((r) => isPersonSubtype(r.subtype)), [data?.rows]);
  const listRows = useMemo(
    () => (data?.rows ?? []).filter((r) => !isPersonSubtype(r.subtype)), [data?.rows]);
  // shared/functional mailboxes at the org's domains (reclassified boxes — org-only, no person)
  const sharedMailboxes = useMemo(() => data?.shared_mailboxes ?? [], [data?.shared_mailboxes]);

  // Re-sort a shallow copy so the toggle is instant (server default = interactions desc).
  const sorted = useMemo(() => {
    const rows = [...personRows];
    if (sort === 'alpha') {
      rows.sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
    } else {
      rows.sort(
        (a, b) =>
          b.interactions - a.interactions ||
          (a.name || a.email || '').localeCompare(b.name || b.email || ''),
      );
    }
    return rows;
  }, [personRows, sort]);

  const visible = showAll ? sorted : sorted.slice(0, PEOPLE_PAGE);

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>
        People
        <span className={styles.cardTitleNote}>via observed employment</span>
        {personRows.length > 0 && (
          <span className={styles.cardSort}>
            <button
              className={`${styles.sortBtn} ${sort === 'interactions' ? styles.sortBtnActive : ''}`}
              onClick={() => setSort('interactions')}
            >
              interactions
            </button>
            <button
              className={`${styles.sortBtn} ${sort === 'alpha' ? styles.sortBtnActive : ''}`}
              onClick={() => setSort('alpha')}
            >
              A–Z
            </button>
          </span>
        )}
      </div>
      {people.isLoading && <div className={styles.loading}>Loading…</div>}
      {people.error != null && <div className={styles.error}>Failed to load people.</div>}
      {data && (
        <>
          {data.rows.length === 0 && sharedMailboxes.length === 0 ? (
            <div className={styles.empty}>No known people at this organization.</div>
          ) : (
            <>
              {personRows.length > 0 ? (
                <>
                  <div className={styles.profilePeopleList}>
                    {visible.map((r) => {
                      const hasStats = r.emails_in + r.emails_out + r.meetings > 0;
                      const cells = (
                        <>
                          <span className={styles.profilePersonIdentity}>
                            <span className={styles.rowName}>{r.name || r.email}</span>
                            <span className={styles.rowSub}>{r.email}</span>
                          </span>
                          <span className={styles.profilePersonTitle} title={r.title || undefined}>
                            {r.title || <span className={styles.subtle}>—</span>}
                            {!r.is_current && <span className={styles.personPastChip}>past</span>}
                          </span>
                          <span className={styles.profilePersonTeam}>
                            {r.division}{r.division && r.group ? ', ' : ''}{r.group ? `[${r.group}]` : ''}
                            {!r.division && !r.group && <span className={styles.subtle}>—</span>}
                          </span>
                          <span className={styles.rowRight}>
                            {hasStats && (
                              <>
                                <span className={styles.statChip} title="emails sent to them">
                                  E↑ {r.emails_out}
                                </span>
                                <span className={styles.statChip} title="emails received from them">
                                  E↓ {r.emails_in}
                                </span>
                                <span className={styles.statChip} title="meetings together">
                                  M {r.meetings}
                                </span>
                              </>
                            )}
                            {r.last_contact_at != null && (
                              <span className={styles.resultDate} title="last contact">
                                {fmtShortDate(r.last_contact_at)}
                              </span>
                            )}
                          </span>
                        </>
                      );
                      // internal-profile-visibility §3.3 — an internal colleague's row keeps every cell
                      // and loses only the click-through: their profile 404s, so a button here would be
                      // a dead end. A div rather than a disabled button, because nothing is disabled —
                      // the row is simply not a link. `cells` is shared so the two cannot drift.
                      return r.profile_hidden === true ? (
                        <div key={r.person_id}
                             className={`${styles.profilePersonRow} ${styles.rowStatic}`}>
                          {cells}
                        </div>
                      ) : (
                        <button key={r.person_id} type="button" className={styles.profilePersonRow}
                                onClick={() => openPerson(r.person_id)}>
                          {cells}
                        </button>
                      );
                    })}
                  </div>
                  {sorted.length > PEOPLE_PAGE && (
                    <button className={styles.loadMore} onClick={() => setShowAll((v) => !v)}>
                      {showAll ? '↑ Show top 10' : `↓ Show all ${sorted.length}`}
                    </button>
                  )}
                </>
              ) : (
                <div className={styles.empty}>No individuals — only the lists/shared mailboxes below.</div>
              )}

              {(listRows.length > 0 || sharedMailboxes.length > 0) && (
                <>
                  <div className={styles.cardTitle} style={{ marginTop: 16 }}>
                    Lists &amp; shared mailboxes
                    <span className={styles.cardTitleNote}>{listRows.length + sharedMailboxes.length}</span>
                  </div>
                  <table className={styles.sideTable}>
                    <tbody>
                      {/* internal-profile-visibility §3.3 — an ember-domain distribution list or shared
                          mailbox is hidden by the same domain rule as a person, so the row drops its
                          role/tabIndex/handlers rather than pointing at a 404. */}
                      {listRows.map((r) => (
                        <tr
                          key={r.person_id}
                          className={r.profile_hidden === true
                            ? `${styles.dealRow} ${styles.rowStatic}` : styles.dealRow}
                          role={r.profile_hidden === true ? undefined : 'button'}
                          tabIndex={r.profile_hidden === true ? undefined : 0}
                          onClick={r.profile_hidden === true ? undefined
                            : () => openPerson(r.person_id)}
                          onKeyDown={r.profile_hidden === true ? undefined : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openPerson(r.person_id);
                            }
                          }}
                        >
                          <td>
                            <div className={styles.rowName}>{r.name || r.email}</div>
                            <div className={styles.rowSub}>{r.email}</div>
                          </td>
                          <td className={styles.rowNum}>
                            <span className={`${styles.chip} ${styles.chipMuted}`}>
                              {subtypeLabel(r.subtype)}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {/* reclassified shared boxes (crm_shared_address) — org-only, no profile to open */}
                      {sharedMailboxes.map((s) => (
                        <tr key={`shared:${s.email}`} className={styles.dealRow}>
                          <td>
                            <div className={styles.rowName}>{s.email}</div>
                          </td>
                          <td className={styles.rowNum}>
                            <span className={`${styles.chip} ${styles.chipMuted}`}>shared mailbox</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
